import React from 'react';
import { motion } from 'framer-motion';
import { useCurrency } from '../hooks/useCurrency';
import { useOrderBumps } from '../hooks/useOrderBumps';
import { CTAButton } from './CTAButton';
const LOCAL_PRICES: Record<string, {
  therapist: string;
  coach: string;
  total: string;
}> = {
  USD: {
    therapist: '$150',
    coach: '$200',
    total: '$350'
  },
  CAD: {
    therapist: 'CA$150',
    coach: 'CA$200',
    total: 'CA$350'
  },
  GBP: {
    therapist: '£100',
    coach: '£130',
    total: '£230'
  },
  AUD: {
    therapist: 'A$180',
    coach: 'A$220',
    total: 'A$400'
  },
  EUR: {
    therapist: '€120',
    coach: '€160',
    total: '€280'
  },
  MXN: {
    therapist: 'MX$1,500',
    coach: 'MX$2,000',
    total: 'MX$3,500'
  },
  COP: {
    therapist: 'COL$250,000',
    coach: 'COL$350,000',
    total: 'COL$600,000'
  },
  ARS: {
    therapist: 'AR$30,000',
    coach: 'AR$45,000',
    total: 'AR$75,000'
  },
  BRL: {
    therapist: 'R$400',
    coach: 'R$550',
    total: 'R$950'
  },
  CLP: {
    therapist: 'CL$80,000',
    coach: 'CL$120,000',
    total: 'CL$200,000'
  },
  PEN: {
    therapist: 'S/250',
    coach: 'S/350',
    total: 'S/600'
  },
  NZD: {
    therapist: 'NZ$170',
    coach: 'NZ$220',
    total: 'NZ$390'
  }
};
export function PriceComparison() {
  const {
    formatPrice,
    currencyCode
  } = useCurrency();
  const {
    goToCheckout
  } = useOrderBumps();
  const prices = LOCAL_PRICES[currencyCode] || LOCAL_PRICES['USD'];
  return <section className="relative py-12 md:py-20 px-6 md:px-8 bg-cream overflow-hidden" data-id="element-406">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-terracotta/[0.03] rounded-full blur-[100px] pointer-events-none" data-id="element-407" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="max-w-lg mx-auto text-center relative z-10" data-id="element-408">
        {/* Ornamental divider */}
        <div className="flex items-center justify-center gap-3 mb-5" data-id="element-409">
          <div className="w-8 md:w-12 h-px bg-gradient-to-r from-transparent to-brown/10" data-id="element-410" />
          <span className="text-brown/15 text-[10px] tracking-[0.3em]" data-id="element-411">✦</span>
          <div className="w-8 md:w-12 h-px bg-gradient-to-l from-transparent to-brown/10" data-id="element-412" />
        </div>

        <p className="text-[10px] uppercase tracking-[0.3em] text-brown/40 font-bold mb-8" data-id="element-413">
          Put it in perspective
        </p>

        {/* Comparison card */}
        <div className="rounded-2xl md:rounded-3xl border border-brown/[0.06] overflow-hidden mb-10" style={{
        background: 'linear-gradient(165deg, #FFFFFF 0%, #FAF7F2 60%, #F5F0E8 100%)',
        boxShadow: '0 1px 3px rgba(61,50,41,0.04), 0 8px 32px rgba(61,50,41,0.05)'
      }} data-id="element-414">
          {/* Top accent strip */}
          <div className="h-[3px] w-full" style={{
          background: 'linear-gradient(90deg, transparent 10%, rgba(61,50,41,0.08) 50%, transparent 90%)'
        }} data-id="element-415" />

          <div className="p-6 md:p-8" data-id="element-416">
            {/* Comparison rows */}
            <div className="space-y-0" data-id="element-417">
              {[{
              label: 'Postpartum therapist',
              price: `${prices.therapist}+`,
              unit: '/ session'
            }, {
              label: 'Nervous system coach',
              price: `${prices.coach}+`,
              unit: '/ hr'
            }].map((item, i) => <div key={i} className="flex items-center justify-between py-4 border-b border-brown/[0.04]" data-id="element-418">
                  <span className="text-brown/65 text-sm" data-id="element-419">{item.label}</span>
                  <span className="text-brown/30 text-sm line-through italic font-serif" data-id="element-420">
                    {item.price}{' '}
                    <span className="text-[11px] not-italic" data-id="element-421">{item.unit}</span>
                  </span>
                </div>)}
            </div>

            {/* Total value */}
            <div className="pt-5 pb-4 text-center" data-id="element-422">
              <p className="text-[10px] uppercase tracking-[0.25em] text-brown/30 font-bold mb-2" data-id="element-423">
                Total value
              </p>
              <span className="text-brown/20 text-2xl md:text-3xl line-through font-serif italic" data-id="element-424">
                {prices.total}+
              </span>
            </div>

            {/* Thin divider */}
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-terracotta/20 to-transparent mx-auto my-4" data-id="element-425" />

            {/* Product price */}
            <div className="py-4 text-center" data-id="element-426">
              <p className="font-serif text-base text-brown/60 mb-2 italic" data-id="element-427">
                Soft Hold
              </p>
              <div className="flex items-baseline justify-center gap-3 mb-1" data-id="element-428">
                <span className="font-serif text-base text-brown/20 line-through italic" data-id="element-429">
                  {formatPrice(96)}
                </span>
                <span className="font-serif text-4xl md:text-5xl font-bold text-terracotta tracking-tight leading-none" style={{
                textShadow: '0 2px 16px rgba(198,114,76,0.12)'
              }} data-id="element-430">
                  {formatPrice(17)}
                </span>
              </div>
              <p className="text-brown/35 text-xs mt-2 uppercase tracking-widest" data-id="element-431">
                One-time payment · Yours forever
              </p>
            </div>
          </div>
        </div>

        {/* Closing line */}
        <p className="font-serif text-lg md:text-xl text-brown/55 italic mb-8 leading-relaxed" data-id="element-432">
          Less than a coffee. Yours forever.
        </p>

        <CTAButton text="Give Me My 30 Minutes" onClick={goToCheckout} data-id="element-433" />

        <div className="flex items-center justify-center gap-3 mt-5 text-[10px] text-brown/35 tracking-wide" data-id="element-434">
          <span data-id="element-435">Instant PDF</span>
          <span className="text-brown/15" data-id="element-436">·</span>
          <span data-id="element-437">Phone-friendly</span>
          <span className="text-brown/15" data-id="element-438">·</span>
          <span data-id="element-439">14-day guarantee</span>
        </div>
      </motion.div>
    </section>;
}